import { useMemo } from 'react';
import { Breakpoint, useBreakpoint, useBreakpointProp } from '@betty/moonlight';
import { RESPONSIVE_TOKENS } from '@betty/moonlight/lib/theme/index.content';

type Params = {
  listWidth: number;
  numberOfColumns: number;
  gridGap: keyof typeof RESPONSIVE_TOKENS.spacing;
  gridOffsetX: keyof typeof RESPONSIVE_TOKENS.spacing;
  isShopItem?: boolean;
};

function useResponsiveGridItemWidth({
  listWidth,
  numberOfColumns,
  gridGap,
  gridOffsetX,
  isShopItem = false,
}: Params) {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === Breakpoint.MOBILE;

  const listMaxWidth = useBreakpointProp({
    mobile: 600,
    tablet: 800,
    desktop: 1000,
  });

  const gapSize = useBreakpointProp({
    mobile: Number(RESPONSIVE_TOKENS.spacing[gridGap].mobile),
    tablet: Number(RESPONSIVE_TOKENS.spacing[gridGap].tablet),
    desktop: Number(RESPONSIVE_TOKENS.spacing[gridGap].desktop),
  });

  const offsetSize = useBreakpointProp({
    mobile: Number(RESPONSIVE_TOKENS.spacing[gridOffsetX].mobile) * 2,
    tablet: Number(RESPONSIVE_TOKENS.spacing[gridOffsetX].tablet) * 2,
    desktop: Number(RESPONSIVE_TOKENS.spacing[gridOffsetX].desktop) * 2,
  });

  const itemWidth = useMemo(() => {
    if (!listWidth) return undefined;

    const scrollbarAllowance = isMobile ? 0 : 15;

    const rowWidth = listWidth - offsetSize - scrollbarAllowance;
    const maxWidth = Math.min(rowWidth, listMaxWidth);

    const totalSpacing =
      gapSize * (numberOfColumns - 1) +
      (isShopItem ? (isMobile ? 0 : offsetSize) : 0);

    const widthPerItem = (maxWidth - totalSpacing) / numberOfColumns;

    return widthPerItem;
  }, [
    listWidth,
    isMobile,
    offsetSize,
    listMaxWidth,
    gapSize,
    numberOfColumns,
    isShopItem,
  ]);

  return itemWidth;
}

export default useResponsiveGridItemWidth;
